/**
 * amplitude 문서 : https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/
 * google analytics 문서: https://firebase.google.com/docs/analytics/get-started?platform=web
 */
import * as amplitude from '@amplitude/analytics-browser';
import { AuthInfoProvider, UserStatus, Workspace } from 'callabo-api/src';
import {
  Analytics as GoogleAnalytics,
  logEvent as gaLogEvent,
  setUserId as gaSetUserId,
  setUserProperties as gaSetUserProperties,
} from 'firebase/analytics';
import { LicensePlan } from 'libs/callabo-state/types/License';
import { Phase } from 'libs/callabo-state/types/Phase';

const PHASE = process.env.NEXT_PUBLIC_PHASE;
const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

interface UserProps {
  email: string;
  workspaceId: string;
  plan: LicensePlan | 'expired';
  status: UserStatus;
  grantType: AuthInfoProvider;
  calendarAuth: IntegrationAuthType;
  accessPreset: Workspace.default_access_scope;
}

export const enum ReSearchType {
  Yes = 'yes',
  No = 'no',
}

export const enum IntegrationAuthType {
  Yes = 'Y',
  No = 'N',
}

export class Analytics {
  private _enableAmplitude = !!AMPLITUDE_API_KEY;

  public googleAnalytics: GoogleAnalytics;

  /** 페이지 진입 루트 */
  public from = '';
  /** 기존 검색결과가 있는 상태에서의 재검색인지 여부 */
  public reSearch: ReSearchType = ReSearchType.No;

  constructor() {
    // init amplitude
    if (this._enableAmplitude) {
      amplitude.init(AMPLITUDE_API_KEY, undefined, {
        defaultTracking: {
          sessions: true,
          pageViews: true,
          formInteractions: true,
          fileDownloads: true,
        },
        autocapture: {
          formInteractions: true,
          attribution: true,
          sessions: true,
          pageViews: true,
          fileDownloads: true,
          elementInteractions: true,
          networkTracking: true,
        },
        minIdLength: 1,
      });
    }
  }

  public setUserId(userId: string): void {
    if (this.googleAnalytics) gaSetUserId(this.googleAnalytics, userId);
    if (this._enableAmplitude) amplitude.setUserId(userId);
  }

  public logEvent(eventName: string, eventProperties?: any | undefined): void {
    if (this.googleAnalytics) gaLogEvent(this.googleAnalytics, eventName, eventProperties);
    if (this._enableAmplitude) amplitude.track(eventName, eventProperties);
  }

  public setVersionName(_version: string): void {
    if (PHASE !== Phase.production) return;
    // setVersionName is not available in the new SDK.
    // App version is automatically collected.
  }

  public setDeviceId(deviceId: string): void {
    if (PHASE !== Phase.production) return;
    amplitude.setDeviceId(deviceId);
  }

  public setUserProperties(props: UserProps): void {
    const properties = {
      email: props.email,
      workspace_slug: props.workspaceId,
      license: props.plan,
      status: props.status,
      grant_type: props.grantType,
      calendar_auth_yn: props.calendarAuth,
      viewer_access_preset: props.accessPreset,
    };
    if (this.googleAnalytics) gaSetUserProperties(this.googleAnalytics, properties);
    if (this._enableAmplitude) {
      const identify = new amplitude.Identify();
      for (const key in properties) {
        identify.set(key, properties[key]);
      }
      amplitude.identify(identify);
    }
  }
}

const analyticsInstance = new Analytics();
export default analyticsInstance;
